/**
 * REFUSAL MESSAGES (#234) — turn a server refusal envelope into a sentence that
 * tells the operator WHAT TO DO.
 *
 * WHY THIS EXISTS. The frontend mapped no refusal code at all, so a refusal on
 * any path that skips login-time routing surfaced as an opaque 403 — or, when
 * the envelope carried no `message`, as nothing whatsoever (#263/#264: four
 * rejected role changes on 2026-08-11 showed the operator a blank screen).
 * With #352 live, forced 2FA now REFUSES a patient_experience account that has
 * not enrolled, so this fires on 15 August, on first login, on the accounts
 * created for the rollout.
 *
 * ⚠ TWO TRANSPORTS, TWO CASINGS, BOTH REAL. The web tier returns
 * `TOTP_SETUP_REQUIRED` (server.js:1344); the mobile bridge returns
 * `totp_setup_required` (routes/mobile_bridge.js:381). #229's shape. They are
 * NOT normalised into one here — normalising would hide the divergence and make
 * the map lie about what the server sends. Both spellings are listed, and a
 * test asserts both resolve.
 *
 * ⚠ THE DEFAULT BRANCH IS THE POINT. The failure mode this guards is a NEW code
 * shipping later and reintroducing silence. An unmapped code renders the code
 * itself plus a next step — ugly, but never blank. `refusalMessage()` is
 * therefore total: it returns a non-empty string for every input, including
 * null, `{}`, and a code nobody has seen.
 */

/** code (as sent, verbatim) → what the operator should DO next. */
const REFUSAL_GUIDANCE = Object.freeze({
  // Forced 2FA enrolment — web casing (server.js:1344)
  TOTP_SETUP_REQUIRED:
    "Set up two-factor authentication to continue. Open 2FA Setup in the sidebar " +
    "and enrol an authenticator app — your role requires it before you can use the system.",
  // …and the mobile bridge casing (routes/mobile_bridge.js:381). Same guidance,
  // deliberately duplicated rather than lower-cased into one lookup.
  totp_setup_required:
    "Set up two-factor authentication to continue. Open 2FA Setup and enrol an " +
    "authenticator app — your role requires it before you can use the system.",

  // Password change gate (#353 — not merged at the time of writing; mapped now so
  // that when it lands it is already spoken for rather than falling to default).
  PASSWORD_CHANGE_REQUIRED:
    "Change your password to continue. You are being asked because this password " +
    "was issued to you and must be replaced before first use.",
  password_change_required:
    "Change your password to continue. You are being asked because this password " +
    "was issued to you and must be replaced before first use.",

  ACCESS_DENIED:
    "You do not have permission to do this. If you need it, ask a system " +
    "administrator to grant it — your role does not include this action.",

  SESSION_EXPIRED:
    "Your session timed out after 15 minutes of inactivity. Sign in again to continue.",

  VALIDATION_FAILED:
    "Some of the details entered are not valid. Correct the highlighted fields and try again.",

  AI_PERMISSION_DENIED:
    "Your role does not include this AI feature. Ask a system administrator if you need it.",

  NO_GRANT:
    "This area needs an explicit grant. Ask a system administrator to add you.",

  DUPLICATE_ADMIN:
    "There can only be one System Administrator account. Use 'sysadmin' for " +
    "additional administrators.",
});

/**
 * @param {object|null|undefined} result a handler response, or anything
 * @returns {string} ALWAYS non-empty
 */
function refusalMessage(result) {
  const code = result && typeof result.code === "string" ? result.code : "";
  const mapped = REFUSAL_GUIDANCE[code];
  if (mapped) return mapped;

  // Unmapped-but-explained: the server bothered to write a sentence, so use it.
  const serverMsg = result && typeof result.message === "string" ? result.message.trim() : "";
  if (serverMsg) return serverMsg;

  // Unmapped and unexplained — the silence case. Say SOMETHING, and name the
  // code so the operator can quote it. Never return "".
  if (code) {
    return `That action was refused (${code}). Please try again, and if it keeps ` +
           `happening quote this code to a system administrator.`;
  }
  return "That action was refused. Please try again, and tell a system administrator " +
         "if it keeps happening.";
}

/** True when the refusal is one the operator can clear themselves. */
function isSelfServiceRefusal(result) {
  const code = (result && result.code) || "";
  return /^(TOTP_SETUP_REQUIRED|totp_setup_required|PASSWORD_CHANGE_REQUIRED|password_change_required|SESSION_EXPIRED)$/.test(code);
}

module.exports = { REFUSAL_GUIDANCE, refusalMessage, isSelfServiceRefusal };
